/**
 * Camera-blocked states — forked VERBATIM from vCIP_droom_4.0/src/flow/components/BlockedCameraCard.tsx
 * (keys renamed). Honest, recovery-stepped: no fake retry that silently no-ops.
 *
 * SPEC_CAPTURE_UX_R2 §7.4: the denied states carry the OS-specific illustrated recovery card (Android Chrome = the
 * lock icon in the address bar; iOS Safari = the Settings path in words) in place of the bare step list.
 */
import { useT } from '@/i18n/runtime';
import type { CaptureCameraApi } from '@/media/useCaptureCamera';
import { RecoveryCard } from '../help/RecoveryCard';
import { Icon } from './Icon';

/**
 * The card's own words, as data — ONE source for what is printed and what is SPOKEN (SPEC_CAPTURE_UX_R2 §3.3:
 * "the same words as the visible card"). A copy change moves both channels at once; there is no second script.
 */
export function blockedCameraWords(t: (k: string) => string, phase: CaptureCameraApi['phase'], detail: string | null): { readonly title: string; readonly body: string } {
  switch (phase) {
    case 'denied':
      return { title: t('sv.cam.denied.title'), body: t('sv.cam.denied.body') };
    case 'os-denied':
      return { title: t('sv.cam.osdenied.title'), body: t('sv.cam.osdenied.body') };
    case 'no-device':
      return { title: t('sv.cam.nodevice.title'), body: detail ? t(detail) : t('sv.cam.nodevice.fallback') };
    case 'webview':
      return { title: t('sv.cam.webview.title'), body: t('sv.cam.webview.body') };
    default:
      return { title: t('sv.cam.unsupported.title'), body: t('sv.cam.unsupported.body') };
  }
}

export function BlockedCameraCard({ cam }: { cam: CaptureCameraApi }): JSX.Element {
  const { t } = useT();
  const { title, body: copy } = blockedCameraWords(t, cam.phase, cam.detail);
  const showSteps = cam.phase === 'denied' || cam.phase === 'os-denied';
  return (
    <div className="card pad" style={{ textAlign: 'center' }} data-testid="blocked-camera">
      <span className="perm-gate-badge">
        <Icon name={cam.phase === 'webview' ? 'monitor' : 'camera'} size={26} />
      </span>
      <div className="h-title sm">{title}</div>
      <p className="sub-sm" style={{ marginTop: 8 }}>
        {copy}
      </p>
      {showSteps ? (
        <div style={{ textAlign: 'start', marginTop: 12 }}>
          <RecoveryCard kind="camera" osLevel={cam.phase === 'os-denied'} />
        </div>
      ) : null}
      {cam.phase === 'webview' ? (
        <p className="perm-autoresume mono" style={{ marginTop: 10 }}>
          {t('sv.perm.webview.tip')}
        </p>
      ) : null}
    </div>
  );
}
